/* eslint-disable react/prop-types */
import { Chip, Tooltip } from '@material-ui/core'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'

import { fetchDeclinedMark, saveDeclinedMark } from 'api'
import { showToast } from 'app/helpers'
import { color, radius } from 'app/theme/tokens'

/**
 * The parent's active "Declined subscription" mark, on their profile.
 *
 * The mark is SET from the Interactions Audit drawer, where the note that
 * justifies it is being written. It has to be READABLE and REMOVABLE from here
 * though: the board is a per-DAY view, so a parent marked three weeks ago only
 * reappears there on a day they visited — anyone wanting to undo a mark would
 * have to remember the visit date and page back to it. People find a family by
 * name, and this is where the name search lands them.
 *
 * Asymmetric on purpose: marking mutes the daily chase and stays narrow;
 * un-marking only ever restores a chase, so it is open to anyone who can open
 * the profile (teachers included, who cannot open the audit board at all).
 *
 * Renders NOTHING when the environment gate is off or there is no active mark,
 * so an un-marked profile is unchanged.
 */
const DeclinedSubscriptionFlag = ({ parentUuid }) => {
  const [mark, setMark] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let live = true
    setMark(null)
    if (!parentUuid) return undefined
    // Non-fatal: a failed read just leaves the chip off. This is an extra
    // signal on a profile that has to render either way.
    fetchDeclinedMark(parentUuid)
      .then((d) => {
        if (live) setMark(d || null)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [parentUuid])

  if (!mark?.enabled || !mark?.active) return null

  const until = mark.expires_on
    ? dayjs(mark.expires_on).format('D MMM YYYY')
    : ''
  // Short form on the chip, full date in the tooltip. The long label used to be
  // one unbreakable run that pushed straight out of the card, so the expiry —
  // the only part that makes the chip actionable — was the bit clipped off.
  const untilShort = mark.expires_on ? dayjs(mark.expires_on).format('D MMM') : ''

  const remove = async () => {
    setSaving(true)
    try {
      await saveDeclinedMark(parentUuid, false)
      setMark((m) => ({ ...m, active: false, expires_on: null }))
      showToast(
        'Mark removed — the daily reminders start again tomorrow',
        'success'
      )
    } catch (error) {
      showToast('Could not remove the mark — try again', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Tooltip
      title={
        until
          ? `The team recorded that this parent said no to a subscription. The daily interaction reminders are paused until ${until}, then start again on their own. Click the cross to remove the mark.`
          : 'The team recorded that this parent said no to a subscription. The daily interaction reminders are paused.'
      }
    >
      {/* Styled to the flag chip already on this card (styles.FlagBox): warm
          cream fill, hairline border, pill radius, 12px Avenir. It was an
          invented green (#2E7D5B on #f0f7f3) that matched nothing else on the
          profile — and green reads as "this is good", which a parent declining
          is not. Removal is the chip's own delete cross rather than a separate
          word underneath, so the label and its action are one object that
          cannot wrap apart. */}
      <Chip
        size="small"
        onDelete={saving ? undefined : remove}
        label={
          untilShort
            ? `Declined subscription · until ${untilShort}`
            : 'Declined subscription'
        }
        style={{
          maxWidth: '100%',
          backgroundColor: color.brandSoft,
          color: color.ink,
          border: `1px solid ${color.border}`,
          borderRadius: radius.pill,
          fontFamily: 'AvenirMedium',
          fontSize: 12,
          opacity: saving ? 0.5 : 1
        }}
      />
    </Tooltip>
  )
}

export default DeclinedSubscriptionFlag
